import type {
  TFooterNavigationLink,
  TFooterNavigationSection,
} from "@/ui/components/navigation/Footer";
import type { TFunctionOf } from "apps/website/types/i18n";
import { routes } from "apps/website/utils/routes";

export type TFooterNavigationTranslator = TFunctionOf<"Components.Footer.Navigation">;

const articleHref = (slug: string) => routes.website.articles.show({ params: { slug } });

export function getFooterPrimaryNavigation(t: TFooterNavigationTranslator): TFooterNavigationSection[] {
  return [
    {
      id: "try-fresh",
      title: t("Primary.TryFresh.title"),
      links: [
        {
          id: "try-fresh-start-my-trial",
          label: t("Primary.TryFresh.startMyTrial"),
          href: routes.website.signUp.fresh(),
        },
        { id: "try-fresh-why-fresh", label: t("Primary.TryFresh.whyFresh"), href: routes.website.whyFresh() },
        {
          id: "try-fresh-refer-a-friend",
          label: t("Primary.TryFresh.referAFriend"),
          href: routes.website.referAFriend({ config: { query: { situation: "web_footer" } } }),
        },
        { id: "try-fresh-reviews", label: t("Primary.TryFresh.reviews"), href: routes.thirdParty.reviews() },
      ],
    },
    {
      id: "about-katkin",
      title: t("Primary.AboutKatKin.title"),
      links: [
        { id: "about-katkin-about-us", label: t("Primary.AboutKatKin.aboutUs"), href: routes.website.aboutUs() },
        {
          id: "about-katkin-products",
          label: t("Primary.AboutKatKin.ourProducts"),
          href: routes.website.products(),
        },
        { id: "about-katkin-blog", label: t("Primary.AboutKatKin.blog"), href: routes.website.articles.index() },
        { id: "about-katkin-careers", label: t("Primary.AboutKatKin.careers"), href: routes.thirdParty.careers() },
      ],
    },
    {
      id: "articles",
      title: t("Primary.Articles.title"),
      links: [
        {
          id: "articles-secret-lives-of-cats",
          label: t("Primary.Articles.secretLivesOfCats"),
          href: articleHref("the-secret-lives-of-cats-weird-and-wonderful-breed-facts"),
        },
        {
          id: "articles-science-behind-fresh-cat-food",
          label: t("Primary.Articles.scienceBehindFreshCatFood"),
          href: articleHref("the-science-behind-fresh-food"),
        },
        {
          id: "articles-whats-wrong-with-cat-food",
          label: t("Primary.Articles.whatsWrongWithCatFood"),
          href: articleHref("what-s-wrong-with-cat-food"),
        },
        {
          id: "articles-understanding-fussy-cats",
          label: t("Primary.Articles.understandingFussyCats"),
          href: articleHref("understanding-fussier-cats"),
        },
      ],
    },
    {
      id: "help",
      title: t("Primary.Help.title"),
      links: [
        { id: "help-my-account", label: t("Primary.Help.myAccount"), href: routes.website.login() },
        { id: "help-faqs", label: t("Primary.Help.faqs"), href: routes.thirdParty.help() },
        { id: "help-contact-us", label: t("Primary.Help.contactUs"), href: routes.thirdParty.contactUs() },
        { id: "help-breeders-club", label: t("Primary.Help.breedersClub"), href: routes.website.breeder() },
      ],
    },
  ];
}

export function getFooterSignedInPrimaryNavigation(t: TFooterNavigationTranslator): TFooterNavigationSection[] {
  return getFooterPrimaryNavigation(t).map((section) => ({
    ...section,
    links: section.links.map((link) => (
      link.id === "help-my-account" ? { ...link, href: routes.website.customer() } : link
    )),
  }));
}

export function getFooterSecondaryNavigation(t: TFooterNavigationTranslator): TFooterNavigationLink[] {
  return [
    {
      id: "terms-and-conditions",
      label: t("Secondary.termsAndConditions"),
      href: routes.website.legal.terms(),
    },
    { id: "privacy", label: t("Secondary.privacy"), href: routes.website.legal.privacyPolicy() },
    { id: "sitemap", label: t("Secondary.sitemap"), href: routes.website.sitemap() },
    { id: "cookie-settings", label: t("Secondary.cookieSettings"), href: routes.website.legal.privacyPolicy() },
  ];
}

export function getFooterSocialNavigation(t: TFooterNavigationTranslator): TFooterNavigationLink[] {
  return [
    {
      id: "email",
      label: t("Social.email"),
      href: routes.thirdParty.email(),
      icon: "email",
      ariaLabel: t("Social.emailAriaLabel"),
    },
    {
      id: "facebook",
      label: t("Social.facebook"),
      href: routes.thirdParty.clubhouse(),
      icon: "facebook",
    },
    {
      id: "instagram",
      label: t("Social.instagram"),
      href: routes.thirdParty.instagram(),
      icon: "instagram",
    },
    {
      id: "youtube",
      label: t("Social.youtube"),
      href: routes.thirdParty.youtube(),
      icon: "youtube",
    },
  ];
}
